import React, { useState } from 'react';
import { FiMove, FiChevronUp, FiChevronDown, FiTrash2, FiPlus } from 'react-icons/fi';
import { Button } from '../../../components/common';
import { useSortable } from '../../cms/components/useSortable';
import { lineKey } from './InvoiceLineItemsTable';
import { formatINR } from '../billingUtils';
import './InvoiceLineItemsTable.css';
import './ProformaLineItemsTable.css';

export const newProformaLine = (hsnCode = '998391', rate = '') => ({
  key: lineKey(),
  description: '',
  hsnCode,
  quantity: '',
  rate,
  discountPercent: '',
});

/**
 * Editable proforma invoice lines: quantity × rate, less discount. Array
 * order is the printed SR No. `lineAmounts` are the server-calculated
 * amounts for each row (display only). `rowErrors` is
 * `{ [index]: { quantity: '…' } }`.
 */
const ProformaLineItemsTable = ({
  items,
  onChange,
  unit = 'MW',
  lineAmounts = [],
  rowErrors = {},
  defaultHsn = '998391',
  disabled = false,
}) => {
  const { move, getItemProps, getItemClass } = useSortable(items, onChange, { disabled });

  // Only the handle starts a drag; a fully draggable row would hijack text
  // selection in its inputs.
  const [armedIndex, setArmedIndex] = useState(null);
  const rowDragProps = (index) => {
    const props = getItemProps(index);
    return {
      ...props,
      draggable: armedIndex === index,
      onDragEnd: (e) => {
        setArmedIndex(null);
        props.onDragEnd?.(e);
      },
    };
  };

  const update = (index, field, value) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  // New lines copy the last line's rate: a PI is usually one rate per unit.
  const addLine = () => onChange([...items, newProformaLine(defaultHsn, items[items.length - 1]?.rate ?? '')]);

  const unitLabel = unit || 'unit';

  return (
    <div className="ili pli">
      <div className="ili-head" aria-hidden="true">
        <span />
        <span>SR</span>
        <span>Description</span>
        <span>HSN/SAC</span>
        <span className="num">Qty ({unitLabel})</span>
        <span className="num">Rate / {unitLabel} (₹)</span>
        <span className="num">Disc. %</span>
        <span className="num">Amount</span>
        <span />
      </div>

      {items.length === 0 && <div className="ili-empty">No lines yet. Add the first line.</div>}

      <ol className="ili-list">
        {items.map((item, index) => {
          const errors = rowErrors[index] || {};
          const amount = lineAmounts[index];
          return (
            <li key={item.key} className={`ili-row ${getItemClass(index)}`} {...rowDragProps(index)}>
              <span
                className="ili-handle"
                title="Drag to reorder"
                onMouseDown={() => !disabled && setArmedIndex(index)}
                onMouseUp={() => setArmedIndex(null)}
              >
                <FiMove />
              </span>
              <span className="ili-sr" aria-label={`Line ${index + 1}`}>{index + 1}</span>

              <div className="ili-cell ili-desc">
                <label className="ili-label" htmlFor={`${item.key}-desc`}>Description</label>
                <textarea
                  id={`${item.key}-desc`}
                  className={`cf-textarea ${errors.description ? 'is-invalid' : ''}`}
                  rows={2}
                  value={item.description}
                  onChange={(e) => update(index, 'description', e.target.value)}
                  placeholder={`e.g. MAL REDDY SUKESH REDDY 2.4 ${unitLabel}`}
                  disabled={disabled}
                />
                {errors.description && <span className="cf-error">{errors.description}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-hsn`}>HSN/SAC</label>
                <input
                  id={`${item.key}-hsn`}
                  className="cf-input"
                  value={item.hsnCode}
                  onChange={(e) => update(index, 'hsnCode', e.target.value)}
                  placeholder={defaultHsn}
                  disabled={disabled}
                />
                {errors.hsn_code && <span className="cf-error">{errors.hsn_code}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-qty`}>Quantity ({unitLabel})</label>
                <input
                  id={`${item.key}-qty`}
                  className={`cf-input ili-price ${errors.quantity ? 'is-invalid' : ''}`}
                  inputMode="decimal"
                  value={item.quantity}
                  onChange={(e) => update(index, 'quantity', e.target.value)}
                  placeholder="0"
                  disabled={disabled}
                />
                {errors.quantity && <span className="cf-error">{errors.quantity}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-rate`}>Rate per {unitLabel} (₹)</label>
                <input
                  id={`${item.key}-rate`}
                  className={`cf-input ili-price ${errors.rate ? 'is-invalid' : ''}`}
                  inputMode="decimal"
                  value={item.rate}
                  onChange={(e) => update(index, 'rate', e.target.value)}
                  placeholder="0.00"
                  disabled={disabled}
                />
                {errors.rate && <span className="cf-error">{errors.rate}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-disc`}>Discount %</label>
                <input
                  id={`${item.key}-disc`}
                  className={`cf-input ili-price ${errors.discount_percent ? 'is-invalid' : ''}`}
                  inputMode="decimal"
                  value={item.discountPercent}
                  onChange={(e) => update(index, 'discountPercent', e.target.value)}
                  placeholder="0"
                  disabled={disabled}
                />
                {errors.discount_percent && <span className="cf-error">{errors.discount_percent}</span>}
              </div>

              <div className="ili-cell">
                <span className="ili-label">Amount</span>
                <span className="pli-amount bl-amount">{amount ? formatINR(amount.amount) : '—'}</span>
                {amount && amount.grossAmount !== amount.amount && (
                  <span className="pli-gross bl-cell-sub">of {formatINR(amount.grossAmount)}</span>
                )}
              </div>

              <div className="ili-row-actions">
                <button type="button" className="action-btn" title="Move up" disabled={disabled || index === 0} onClick={() => move(index, index - 1)}>
                  <FiChevronUp />
                </button>
                <button type="button" className="action-btn" title="Move down" disabled={disabled || index === items.length - 1} onClick={() => move(index, index + 1)}>
                  <FiChevronDown />
                </button>
                <button type="button" className="action-btn delete" title="Remove line" disabled={disabled} onClick={() => remove(index)}>
                  <FiTrash2 />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="ili-footer">
        <Button variant="outline" size="small" icon={<FiPlus />} onClick={addLine} disabled={disabled}>
          Add line
        </Button>
      </div>
    </div>
  );
};

export default ProformaLineItemsTable;
